// Date formatting utilities
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatShortDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Priority and status utilities
export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'Critical':
      return '#f44336'; // Red
    case 'High':
      return '#ff9800'; // Orange
    case 'Medium':
      return '#2196f3'; // Blue
    case 'Low':
      return '#4caf50'; // Green
    default:
      return '#9e9e9e'; // Gray
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Open':
      return '#f44336'; // Red
    case 'InProgress':
      return '#ff9800'; // Orange
    case 'Resolved':
      return '#4caf50'; // Green
    case 'Closed':
      return '#9e9e9e'; // Gray
    default:
      return '#9e9e9e'; // Gray
  }
};

export const getPriorityText = (priority: string): string => {
  switch (priority) {
    case 'Critical':
      return 'Kritik';
    case 'High':
      return 'Yüksek';
    case 'Medium':
      return 'Orta';
    case 'Low':
      return 'Düşük';
    default:
      return priority;
  }
};

export const getStatusText = (status: string): string => {
  switch (status) {
    case 'Open':
      return 'Açık';
    case 'InProgress':
      return 'İşlemde';
    case 'Resolved':
      return 'Çözümlendi';
    case 'Closed':
      return 'Kapatıldı';
    default:
      return status;
  }
};

// File utilities
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  const extension = fileName.toLowerCase().substr(fileName.lastIndexOf('.'));
  return imageExtensions.includes(extension);
};

// Role and permission utilities
export const hasRole = (userRoles: string[], requiredRole: string): boolean => {
  return userRoles.includes(requiredRole) || userRoles.includes('SuperAdmin');
};

export const canPerformAction = (
  userRoles: string[],
  categoryPermissions: any[],
  categoryId: number,
  action: 'view' | 'edit' | 'delete'
): boolean => {
  // SuperAdmin can do everything
  if (userRoles.includes('SuperAdmin')) {
    return true;
  }

  // Check category-specific permissions
  const permission = categoryPermissions.find(p => p.categoryId === categoryId);
  if (!permission) {
    return false;
  }

  switch (action) {
    case 'view':
      return permission.canView;
    case 'edit':
      return permission.canEdit;
    case 'delete':
      return permission.canDelete;
    default:
      return false;
  }
};

// Form validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (password.length < 6) {
    errors.push('Şifre en az 6 karakter olmalıdır');
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push('Şifrede en az bir küçük harf bulunmalıdır');
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push('Şifrede en az bir büyük harf bulunmalıdır');
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push('Şifrede en az bir rakam bulunmalıdır');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^(\+90|0)?[5-9]\d{9}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// URL and QR utilities
export const generateQRCodeUrl = (locationName: string, baseUrl: string = window.location.origin): string => {
  const encodedLocation = encodeURIComponent(locationName);
  return `${baseUrl}/?location=${encodedLocation}`;
};

export const getQRCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('location');
};

// QR Code utilities
export const generateAllQRCodeUrls = (): { location: string; url: string; qrCode: string }[] => {
  const locations = [
    { name: "Acil Servis", qrCode: "QR_ACIL_01" },
    { name: "Dahiliye Servisi", qrCode: "QR_DAH_01" },
    { name: "Cerrahi Servisi", qrCode: "QR_CER_01" },
    { name: "Kadın Doğum", qrCode: "QR_KD_01" },
    { name: "Yoğun Bakım", qrCode: "QR_YB_01" },
    { name: "Kafeterya", qrCode: "QR_KAF_01" }
  ];
  
  return locations.map(loc => ({
    location: loc.name,
    url: generateQRCodeUrl(loc.name),
    qrCode: loc.qrCode
  }));
};

// Local storage utilities
export const setLocalStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage:', error);
  }
};

export const getLocalStorage = (key: string, defaultValue: any = null): any => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return defaultValue;
  }
};

export const removeLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage:', error);
  }
};

// Debounce utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void => {
  let timeoutId: number;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => func.apply(null, args), delay);
  };
};